import type React from 'react';
import { css, styled } from 'styled-components';
import type { SearchResultProps } from './types';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { useQueryResourceContextSearchExternal } from '@fabric-app/business/resources/queries/useQueryResourceContextSearchExternal';
import { FloatingFooterSaveAsSmartCollection } from '../FloatingFooterSaveAsSmartCollection/FloatingFooterSaveAsSmartCollection';
import { Flex } from '@fabric-app/ui/components/Flex';
import { IconNavigational24Connections, IconNavigational24HomeIcon } from '@fabric-app/icons/svg';
import { SearchResultsFabricItems } from './SearchResultsFabricItems';
import { SearchResultsExternalItems } from './SearchResultsExternalItems';
import { useQueryIntegrations } from '@fabric-app/business/connections/queries/useQueryIntegrations';
import { RESOURCE_GRID_PREVIEW_CONTENT } from '@fabric-app/ui/theme/resourcePreviewConstants';
import { useResourceListPreferencesContext } from '@fabric-app/business/resources/components/ResourceList/contexts/resourceListContextPreferences';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { useSearchOverlayStore } from '@fabric-app/business/search/hooks/useSearchOverlay';
import { DragResizeHandle } from '../../DragResizeHandle/DragResizeHandle';
import { usePanning } from '@fabric-app/ui/hooks/usePanning';
import { motion, useMotionValue } from 'framer-motion';
import { mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { shallow } from 'zustand/shallow';
import { useDebouncedCallback } from '@fabric-app/hooks/useDebouncedCallback';
import { pick } from '@fabric-app/utils/object/manipulation';
import { useContextShareToken } from '@fabric-app/business/permission/contexts/AnonShareTokenContext';

const MIN_EXTERNAL_SEARCH_WIDTH = 370;
const MAX_EXTERNAL_SEARCH_WIDTH = 800;
const CSS_CONTAINER_NAME = 'search-results-desktop';

const getClampedWidth = (nextWidth: number = 0) => {
  return Math.min(MAX_EXTERNAL_SEARCH_WIDTH, Math.max(MIN_EXTERNAL_SEARCH_WIDTH, nextWidth));
};

export const SearchResultsDesktop: React.FC<SearchResultProps> = (props) => {
  const {
    isLoading,
    total,
    queryArgs,
    isSaveAsSmartCollectionEnabled,
    filters,
    zoomLevel,
    queryHash,
  } = props;
  const { viewMode } = useResourceListPreferencesContext();
  const searchedText = 'text' in queryArgs ? queryArgs.text : '';

  const { hideExternalSearch } = useSearchOverlayStore(
    (state) => pick(state, ['hideExternalSearch']),
    shallow,
  );
  const { integrations } = useQueryIntegrations();
  const hasSearchBasedIntegrations = integrations.some(
    (integration) => integration.viewConfig.isSearchBased,
  );

  const showExternalSearch = hasSearchBasedIntegrations && !!searchedText && !hideExternalSearch;

  const {
    resources: externalResources,
    total: externalTotal,
    isLoading: isLoadingExternal,
  } = useQueryResourceContextSearchExternal(
    {
      query: searchedText,
    },
    {
      enabled: !!searchedText && showExternalSearch,
    },
  );

  const maxContainerWidth =
    viewMode === 'Detailed'
      ? zoomLevel * RESOURCE_GRID_PREVIEW_CONTENT.width * 0.5 + 740
      : undefined;

  const motionWidth = useMotionValue(
    getClampedWidth(useSearchOverlayStore.getState().externalSearchWidth),
  );
  const setExternalSearchWidthDebounced = useDebouncedCallback(
    useSearchOverlayStore.getState().setExternalSearchWidth,
    1000,
  );
  const { motionProps, isPanningX } = usePanning({
    preventEventsDefault: true,
    stopEventsPropagation: true,
    touchOnly: false,
    onPan: (_e, info, moreInfo) => {
      if (moreInfo.axis === 'x') {
        const nextValue = getClampedWidth(motionWidth.get() - info.delta.x);
        setExternalSearchWidthDebounced(nextValue);
        motionWidth.set(nextValue);
      }
    },
  });

  const shareToken = useContextShareToken();

  return (
    <Layout>
      <Column
        scrollbarVariant="subtle"
        orientation="both"
        style={{
          pointerEvents: isLoading ? 'none' : undefined,
          opacity: isLoading ? 0.5 : 1,
          transition: 'opacity 0.2s ease-in-out',
          flexGrow: 1,
        }}
        data-multiselect-allow
        data-multiselect-layer-id={'searchbar-with-overlay'}
        key={queryHash}
      >
        {!shareToken && (
          <FixPositionContainer>
            <FloatingFooterSaveAsSmartCollection
              text={searchedText}
              filters={filters}
              enabled={isSaveAsSmartCollectionEnabled}
            />
          </FixPositionContainer>
        )}

        <ContentContainer
          style={{
            maxWidth: maxContainerWidth,
          }}
        >
          <ResultHeader
            isHidden={isLoading && total === 0}
            style={
              viewMode === 'List'
                ? {
                    marginBottom: 0,
                    paddingBottom: 32,
                  }
                : undefined
            }
          >
            <IconNavigational24HomeIcon />
            <ResultHeaderText>
              {total} results {shareToken ? 'found' : 'from your Fabric'}
            </ResultHeaderText>
          </ResultHeader>
          <SearchResultsFabricItems {...props} />
        </ContentContainer>
      </Column>
      {!shareToken && showExternalSearch && (
        <ExternalContainer style={{ width: motionWidth }}>
          <DragableArea {...motionProps} isDragging={isPanningX}>
            <DragHandleContainer isDragging={isPanningX}>
              <DragResizeHandle.Thumb />
            </DragHandleContainer>
          </DragableArea>
          <Column
            scrollbarVariant="subtle"
            style={{
              pointerEvents: isLoading ? 'none' : undefined,
              opacity: isLoading ? 0.5 : 1,
              transition: 'opacity 0.2s ease-in-out',
              flexBasis: 450,
              borderLeft: `1px solid ${cssVar['color-border-secondary']}`,
              flexGrow: 0,
              background: cssVar['color-bg-secondary'],
              height: '100%',
            }}
            key={`${queryHash}-external`}
          >
            <ContentContainer>
              <ResultHeader isHidden={isLoading && externalTotal === 0}>
                <IconNavigational24Connections />
                <ResultHeaderText>{externalTotal} results from connected sources</ResultHeaderText>
              </ResultHeader>
              <SearchResultsExternalItems
                {...props}
                isLoading={isLoadingExternal}
                resources={externalResources}
                total={externalTotal}
              />
            </ContentContainer>
          </Column>
        </ExternalContainer>
      )}
    </Layout>
  );
};

const ExternalContainer = styled(motion.div)`
  position: relative;
  position: relative;
  height: 100%;
  z-index: 10;
`;

const DragableArea = styled(motion.div)<{ isDragging: boolean }>`
  position: absolute;
  top: 0;

  left: -2px;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
  /* highlight */
  &:after {
    content: '';
    display: block;
    position: absolute;
    width: 2px;
    height: 100%;
    top: 0;
    left: 2px;
    background: transparent;
  }
  &:hover {
    &:after {
      background: ${cssVar['color-app-primary']};
    }
  }

  ${({ isDragging }) =>
    isDragging &&
    css`
      cursor: grabbing;
      &:after {
        background: ${cssVar['color-app-primary']};
      }
      &:before {
        cursor: grabbing;
        content: '';
        display: block;
        position: absolute;
        inset: -10px;
      }
    `}
  z-index: 10;
`;
const DragHandleContainer = styled(motion.div)<{ isDragging: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 6px;
  z-index: 10;
  cursor: grab;
  ${({ isDragging }) =>
    isDragging &&
    css`
      cursor: grabbing;
    `}
  &:before {
    content: '';
    display: block;
    position: absolute;
    inset: -10px;
  }
  ${mediaHover} {
    &:hover {
      ${DragResizeHandle.Thumb} {
        opacity: 1;
        height: 60px;
      }
    }
  }

  ${(p) =>
    p.isDragging &&
    css`
      cursor: grabbing;
      ${DragResizeHandle.Thumb} {
        opacity: 1;
        height: 60px;
      }
    `}
`;

const Layout = styled.div`
  display: flex;
  overflow: hidden;
  flex-grow: 1;
`;

const FixPositionContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(0);
  z-index: 130;
`;

const ResultHeader = styled(Flex).attrs({
  alignItems: 'center',
  gap: 10,
})<{ isHidden?: boolean }>`
  padding: 40px 42px 0 42px;
  margin-bottom: -14px;
  color: ${cssVar['color-text-primary']};
  ${({ isHidden }) =>
    isHidden &&
    css`
      opacity: 0;
    `}
`;

const ResultHeaderText = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: ${cssVar['color-text-primary']};
`;

const Column = styled(ScrollArea.Default)`
  flex-grow: 1;
  container-name: ${CSS_CONTAINER_NAME};
  container-type: inline-size;

  [data-radix-scroll-area-viewport] {
    height: 100% !important;
    display: flex;
    & > div {
      display: flex !important;
      flex-grow: 1;
    }
  }
`;

const ContentContainer = styled.div`
  min-height: 50vh;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  @container ${CSS_CONTAINER_NAME} (max-width: 1024px) {
    margin-left: 0;
  }
`;
